import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api from "../../api/axiosClient";
import { useAuth } from "../../context/AuthContext";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Raleway:wght@300;400;500;600;700;800;900&display=swap');
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

/* Reset is scoped to this page so it can't leak into the navbar/footer */
.hl-root, .hl-root *, .hl-root *::before, .hl-root *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.hl-root {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  font-family: 'Raleway', sans-serif;
  background: #ffffff;
}

.hl-left {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 56px 60px;
  overflow: hidden;
  background:
    linear-gradient(rgba(0,0,0,0.70), rgba(0,0,0,0.70)),
    url('https://images.unsplash.com/photo-1521791136064-7986c2920216?q=80&w=1600&auto=format&fit=crop')
    center center/cover no-repeat;
}

.hl-headline {
  position: relative;
  z-index: 1;
  font-size: clamp(2rem, 3vw, 2.8rem);
  font-weight: 900;
  color: #fff;
  line-height: 1.1;
  margin-bottom: 18px;
  letter-spacing: -.03em;
}

.hl-headline em {
  font-style: italic;
  color: #fff000;
  font-weight: 800;
}

.hl-desc {
  position: relative;
  z-index: 1;
  font-size: .9rem;
  color: rgba(255,255,255,.82);
  line-height: 1.85;
  max-width: 380px;
  margin-bottom: 48px;
  font-weight: 400;
}

.hl-perks {
  position: relative;
  z-index: 1;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 13px;
}

.hl-perk {
  display: flex;
  align-items: center;
  gap: 11px;
  font-size: .82rem;
  color: rgba(255,255,255,.88);
  font-weight: 500;
}

.hl-tick {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex-shrink: 0;
  background: rgba(255,240,0,.14);
  border: 1.5px solid rgba(255,240,0,.35);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: .5rem;
  color: #fff000;
}

.hl-right {
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px 68px;
}

.hl-box {
  width: 100%;
  max-width: 420px;
}

.hl-title {
  font-size: 2rem;
  font-weight: 900;
  color: #000000;
  margin-bottom: 5px;
  letter-spacing: -.03em;
}

.hl-sub {
  font-size: .84rem;
  color: #64748b;
  margin-bottom: 32px;
  font-weight: 500;
}

.hl-err {
  background: #fef2f2;
  border: 1.5px solid #fecaca;
  border-radius: 10px;
  padding: 11px 14px;
  margin-bottom: 20px;
  font-size: .8rem;
  color: #b91c1c;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.hl-field {
  margin-bottom: 18px;
}

.hl-lbl {
  display: block;
  font-size: .68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: #475569;
  margin-bottom: 7px;
}

.hl-inp {
  width: 100%;
  padding: 13px 15px;
  font-size: .9rem;
  font-weight: 500;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  color: #1e293b;
  font-family: 'Raleway', sans-serif;
  outline: none;
  transition: border-color .2s, box-shadow .2s;
}

.hl-inp:focus {
  border-color: #fff000;
  box-shadow: 0 0 0 4px rgba(255,240,0,.12);
}

.hl-inp:disabled {
  opacity: .55;
  cursor: not-allowed;
}

.hl-pw-wrap {
  position: relative;
}

.hl-eye {
  position: absolute;
  right: 13px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  font-size: .84rem;
  padding: 4px;
  transition: color .15s;
  line-height: 1;
}

.hl-eye:hover {
  color: #000000;
}

.hl-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.hl-ck-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.hl-ck {
  width: 15px;
  height: 15px;
  accent-color: #000000;
  cursor: pointer;
}

.hl-ck-lbl {
  font-size: .8rem;
  color: #64748b;
  cursor: pointer;
  font-weight: 500;
}

.hl-forgot {
  font-size: .8rem;
  font-weight: 700;
  color: #000000;
  text-decoration: none;
}

/* yellow text on white is unreadable, so use a yellow underline instead */
.hl-forgot:hover {
  text-decoration: underline;
  text-decoration-color: #fff000;
  text-decoration-thickness: 3px;
  text-underline-offset: 4px;
}

.hl-btn {
  width: 100%;
  padding: 14px;
  font-size: .92rem;
  font-weight: 800;
  background: #fff000;
  color: #000000;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-family: 'Raleway', sans-serif;
  transition: all .2s;
  box-shadow: 0 6px 22px rgba(255,240,0,.35);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  letter-spacing: -.01em;
}

.hl-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 12px 36px rgba(255,240,0,.45);
  filter: brightness(1.05);
}

.hl-btn:disabled {
  opacity: .5;
  cursor: not-allowed;
  transform: none;
}

@keyframes hl-spin {
  to {
    transform: rotate(360deg);
  }
}

.hl-spin {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2.5px solid rgba(0,0,0,.2);
  border-top-color: #000000;
  border-radius: 50%;
  animation: hl-spin .7s linear infinite;
}

.hl-div {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 26px 0;
}

.hl-div-line {
  flex: 1;
  height: 1px;
  background: #e2e8f0;
}

.hl-div-txt {
  font-size: .68rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: .08em;
}

.hl-signup {
  text-align: center;
  font-size: .83rem;
  color: #64748b;
}

.hl-signup a {
  color: #000000;
  font-weight: 800;
  text-decoration: none;
  border-bottom: 2px solid #fff000;
  padding-bottom: 1px;
  transition: background .15s;
}

.hl-signup a:hover {
  background: #fff000;
  color: #000000;
}

@media (max-width: 780px) {
  .hl-root {
    grid-template-columns: 1fr;
  }

  .hl-left {
    display: none;
  }

  .hl-right {
    padding: 44px 24px;
    background: #fff;
  }
}

@media (max-width: 420px) {
  .hl-right {
    padding: 32px 18px;
  }

  .hl-title {
    font-size: 1.6rem;
  }

  .hl-btn, .hl-inp {
    font-size: 1rem;
  }

  .hl-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}
`;

// Google client IDs are public (not secrets), so it's fine to keep this in the bundle.
// It MUST match GOOGLE_CLIENT_ID in the Django settings.
const GOOGLE_CLIENT_ID =
  "551247510793-ria1stm1obcn36nkkl2is4tknoqaj2sv.apps.googleusercontent.com";

const GSI_SRC = "https://accounts.google.com/gsi/client";

// Loads Google Identity Services if index.html doesn't already do it.
const ensureGoogleScript = () => {
  if (window.google?.accounts?.id) return;
  if (document.querySelector(`script[src="${GSI_SRC}"]`)) return;

  const s = document.createElement("script");
  s.src = GSI_SRC;
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
};

const firstMsg = (v) => {
  if (Array.isArray(v)) return firstMsg(v[0]);
  if (v && typeof v === 'object') return firstMsg(Object.values(v)[0]);
  return String(v ?? '');
};

// Turns any DRF error response into one readable message.
// Handles {detail}, {non_field_errors: [...]} and per-field errors.
const readApiError = (err, fallback) => {
  if (!err.response) {
    return 'Cannot reach the server. Check your connection and try again.';
  }

  if (err.response.status === 429) {
    return 'Too many attempts. Please wait a minute and try again.';
  }

  const d = err.response.data;

  if (d && typeof d === 'object') {
    if (d.detail) return firstMsg(d.detail);
    if (d.non_field_errors) return firstMsg(d.non_field_errors);

    const any = Object.values(d)[0];
    if (any) return firstMsg(any);
  }

  return fallback;
};

const HireLogin = () => {
  const [form, setForm] = useState({
    email: '',
    password: '',
    remember: false
  });

  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { login } = useAuth();
  const googleButtonRef = useRef(null);
  const googleHandlerRef = useRef(null);

  const fromPath = location.state?.from?.pathname;

  const from =
    fromPath && fromPath !== '/HireLogin'
      ? fromPath
      : '/hire';

  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;

    setForm(prev => ({
      ...prev,
      [id]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCredentialResponse = async (response) => {
    const token = response?.credential;

    if (!token) {
      setError("Invalid Google credential. Please try again.");
      return;
    }

    setError('');
    setLoading(true);

    try {
      const { data } = await api.post("/google-login/", {
        token,
        role: "client",
        remember: form.remember,
      });

      await login();

      // If Google just created this account, it has no phone number yet.
      navigate(from, {
        replace: true,
        state: data?.needs_onboarding ? { needsOnboarding: true } : undefined,
      });
    } catch (err) {
      console.error(
        "Client Google login failed:",
        err.response?.status,
        err.response?.data || err.message
      );

      setError(readApiError(err, "Google sign-in failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  googleHandlerRef.current = handleCredentialResponse;

  useEffect(() => {
    let cancelled = false;
    let tries = 0;
    let timeoutId;

    ensureGoogleScript();

    const setup = () => {
      if (cancelled) return;

      if (window.google?.accounts?.id && googleButtonRef.current) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response) => googleHandlerRef.current?.(response),
        });

        window.google.accounts.id.renderButton(googleButtonRef.current, {
          theme: "outline",
          size: "large",
          type: "standard",
          text: "signin_with",
          width: 320,
        });

        return;
      }

      tries += 1;
      if (tries < 40) {
        timeoutId = window.setTimeout(setup, 250);
      } else {
        setError("Google sign-in is currently unavailable. Please use your email and password.");
      }
    };

    setup();

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.email.trim() || !form.password) {
      setError('Both fields are required.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.post('/client-login/', {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        remember: form.remember
      });

      await login();
      navigate(from, { replace: true });
    } catch (err) {
      // Shows the real reason, e.g. "Access denied. Please use the craftsman login page."
      setError(readApiError(err, 'Incorrect email or password.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{CSS}</style>

      <div className="hl-root">
        <div className="hl-left">
          <h1 className="hl-headline">
            Hire skilled
            <br />
            craftsmen.
            <br />
            <em>Get it done right.</em>
          </h1>

          <p className="hl-desc">
            Kenya's verified marketplace for craftsmen and artisans.
            Browse portfolios, read reviews, and hire directly.
          </p>

          <ul className="hl-perks">
            {[
              'Every craftsman is manually verified',
              'Real portfolios & honest reviews',
              'Direct hire — no commission for clients',
              'Track your job from request to completion',
            ].map((text, i) => (
              <li className="hl-perk" key={i}>
                <span className="hl-tick">
                  <i className="fas fa-check" />
                </span>
                {text}
              </li>
            ))}
          </ul>
        </div>

        <div className="hl-right">
          <div className="hl-box">
            <h2 className="hl-title">Welcome back</h2>

            <p className="hl-sub">
              Log in to your client account
            </p>

            {error && (
              <div className="hl-err" role="alert">
                <i className="fas fa-exclamation-circle" />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="hl-field">
                <label className="hl-lbl" htmlFor="email">
                  Email address
                </label>

                <input
                  id="email"
                  type="email"
                  className="hl-inp"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  autoComplete="email"
                />
              </div>

              <div className="hl-field">
                <label className="hl-lbl" htmlFor="password">
                  Password
                </label>

                <div className="hl-pw-wrap">
                  <input
                    id="password"
                    type={showPw ? 'text' : 'password'}
                    className="hl-inp"
                    placeholder="••••••••"
                    value={form.password}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    style={{ paddingRight: 44 }}
                    autoComplete="current-password"
                  />

                  <button
                    type="button"
                    className="hl-eye"
                    onClick={() => setShowPw(!showPw)}
                    tabIndex="-1"
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                  >
                    <i className={`fas ${showPw ? 'fa-eye-slash' : 'fa-eye'}`} />
                  </button>
                </div>
              </div>

              <div className="hl-row">
                <div className="hl-ck-wrap">
                  <input
                    id="remember"
                    type="checkbox"
                    className="hl-ck"
                    checked={form.remember}
                    onChange={handleChange}
                  />

                  <label className="hl-ck-lbl" htmlFor="remember">
                    Keep me logged in
                  </label>
                </div>

                <Link to="/forgot-password" className="hl-forgot">
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                className="hl-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="hl-spin" />
                    Logging in…
                  </>
                ) : (
                  <>
                    <i className="fas fa-arrow-right-to-bracket" />
                    Log in
                  </>
                )}
              </button>
            </form>

            <div className="hl-div">
              <div className="hl-div-line" />
              <span className="hl-div-txt">Or continue with</span>
              <div className="hl-div-line" />
            </div>

            <div
              ref={googleButtonRef}
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 24,
                minHeight: 40,
              }}
            />

            <div className="hl-div">
              <div className="hl-div-line" />
              <span className="hl-div-txt">New here?</span>
              <div className="hl-div-line" />
            </div>

            <p className="hl-signup">
              No account yet?{" "}
              <Link to="/HireSignup">
                Create one — it's free
              </Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default HireLogin;
